import { Box, BoxProps, styled } from '@mui/material';

export interface ResizeHandleProps extends BoxProps {
  axis: 'x' | 'y';
  isDragging?: boolean;
}

/**
 * The app's resize handle: invisible hit area at rest, a centered pill on hover. `axis="x"`
 * is a vertical strip (navbar), `axis="y"` a horizontal one (composer dock). Spread
 * `react-resizable-layout`'s `separatorProps` onto it for the drag wiring.
 */
export const ResizeHandle = styled(Box, {
  name: 'ResizeHandle',
  shouldForwardProp: (prop) => prop !== 'axis' && prop !== 'isDragging',
})<ResizeHandleProps>(({ theme, axis, isDragging }) => {
  const isX = axis === 'x';

  return {
    position: 'absolute',
    touchAction: 'none',
    ...(isX
      ? { top: 0, bottom: 0, width: '0.5rem', cursor: 'col-resize' }
      : { left: 0, right: 0, height: '0.75rem', cursor: 'ns-resize' }),

    '&::before': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      borderRadius: '2px',
      opacity: 0,
      backgroundColor: isX
        ? theme.palette.custom.OV.sys.outline
        : theme.palette.custom.OV.sys.outlineVariant,
      transition: theme.transitions.create(
        ['opacity', 'background-color', 'width', 'height'],
        {
          duration: theme.transitions.duration.shorter,
        },
      ),
      ...(isX
        ? { width: '2px', height: '2.25rem' }
        : { width: '2.5rem', height: '0.1875rem' }),
    },

    '&:hover::before': isX
      ? { opacity: 0.9, height: '3rem' }
      : { opacity: 1, backgroundColor: theme.palette.custom.OV.sys.outline, width: '3.5rem' },

    ...(isDragging && {
      '&::before, &:hover::before': {
        opacity: 1,
        backgroundColor: theme.palette.custom.OV.sys.secondary,
        ...(isX ? { height: '4.5rem' } : { width: '4.5rem' }),
      },
    }),
  };
});
